/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

// Lib
import { apiClient } from '@lib/api-client';
import {
	queryClient,
	TMutationConfig,
} from '@lib/react-query';

import { queryKeys } from '@config/constants/query-keys';
import {
	TApiResponseCreateUser,
	TBodyCreateUser,
} from '@features/resources/producers/types';
import { TApiError } from '@global-types/api/api';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const addUser = (
	body: TBodyCreateUser
): Promise<TApiResponseCreateUser> => {
	return apiClient.post('/users', body);
};

/* ---------------------------------------------------------------------------- */
/*                                     Type                                    */
/* -------------------------------------------------------------------------- */

type TUseAddUserOptions = {
	mutationConfig?: TMutationConfig<typeof addUser>;
	page: number;
	size: number;
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useAddUser = ({
	page,
	size,
	mutationConfig,
}: TUseAddUserOptions) => {
	const { t } = useTranslation();
	const { onSuccess, onError } = mutationConfig || {};
	const {
		mutate,
		isPending: isLoading,
		isSuccess,
	} = useMutation({
		mutationFn: addUser,
		onSuccess: async (...args) => {
			onSuccess?.(...args);
			notifications.show({
				title: t('global.success'),
				message: t(
					'users.notifications.userSuccessfullyAdded'
				),
				color: 'var(--mantine-color-igluGreen-9)',
			});

			queryClient.invalidateQueries({
				queryKey: [
					queryKeys.app.users.list.key,
					{ page: page, size: size },
				],
			});
		},
		onError: (error: TApiError, variables, context) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'var(--mantine-color-igluRed-9)',
			});
			onError?.(error, variables, context);
		},
	});

	return { mutate, isLoading, isSuccess };
};
